import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchCartJson } from '@theme/cart-state';
import { productItemCount } from '@theme/cart-contract';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** Tracks the newest cart-update so stale async fallbacks can be discarded. */
  #cartUpdateSeq = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events: keeps the header cart-count badge in sync and
   * announces the new count to screen readers.
   *
   * The badge counts PRODUCTS (EA-3912), so the count is derived from the cart's
   * lines rather than read from `item_count` — that field counts the CYO
   * flavours, add-ons, gift wrap, gift message, greeting/digital cards and the
   * tip, which is how one arrangement with a gift message put a 2 on the icon.
   * Same rule as `snippets/cart-product-count.liquid`, which renders the badge
   * on a full page load.
   *
   * @param {CustomEvent<{ resource?: { items?: Array<object> } }>} event
   */
  #onCartUpdate = async (event) => {
    const seq = ++this.#cartUpdateSeq;

    // Some cart sources (e.g. the EA cart drawer) dispatch without a resource,
    // so fall back to fetching the current cart to count its lines.
    let cartCount = productItemCount(event.detail.resource);
    if (cartCount == null) {
      try {
        // Shared with every other reader reacting to this same event (@theme/cart-state).
        const cart = await fetchCartJson();
        cartCount = productItemCount(cart);
      } catch (_) {
        return;
      }
      // A newer cart update started while fetching — discard this stale result.
      if (seq !== this.#cartUpdateSeq) return;
    }

    if (cartCount == null) return;

    this.#updateCartBadge(cartCount);
    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };

  /**
   * Updates the header cart-count badge.
   * @param {number} count - The number of items in the cart.
   */
  #updateCartBadge = (count) => {
    const badge = this.querySelector('[data-cart-count]');
    if (!badge) return;

    badge.textContent = count > 99 ? '99+' : String(count);
    badge.classList.toggle('header-cart-count--overflow', count > 99);
    badge.classList.toggle('hidden', count === 0);
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
